import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Lightbulb, 
  Sparkles, 
  Gem, 
  ClipboardList, 
  Gift, 
  Home, 
  Settings, 
  Building2, 
  Palette,
  MessageCircle,
  PenTool,
  CheckCircle2,
  Send,
  ArrowRight
} from 'lucide-react';
import SEO from '../components/SEO';
import './ProyectoPersonalizado.css';

const ProyectoPersonalizado = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipoProyecto: '',
    descripcion: '',
    aceptaTerminos: false,
    honeypot: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formStartTime] = useState(Date.now());

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot protection: Honeypot check
    if (formData.honeypot) return;
    
    // Bot protection: Minimum time to fill form (3 seconds)
    const timeToFill = Date.now() - formStartTime;
    if (timeToFill < 3000) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Por favor, espera unos segundos antes de enviar el formulario.' 
      });
      return;
    }
    
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.email.trim()) newErrors.email = 'El email es obligatorio';
    if (!formData.tipoProyecto) newErrors.tipoProyecto = 'Selecciona el tipo de proyecto';
    if (!formData.descripcion.trim()) newErrors.descripcion = 'La descripción es obligatoria';
    if (!formData.aceptaTerminos) newErrors.aceptaTerminos = 'Debes aceptar los términos';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/project', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      
      if (data.success) {
        setSubmitStatus({ type: 'success', message: data.message });
        setFormData({ 
          nombre: '', email: '', telefono: '', tipoProyecto: '', 
          descripcion: '', aceptaTerminos: false, honeypot: '' 
        });
      } else {
        setSubmitStatus({ type: 'error', message: data.message || 'Error al enviar' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Error de conexión' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Proyecto Personalizado",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Artelaser3D",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Asturias",
        "addressRegion": "Asturias",
        "addressCountry": "ES"
      }
    },
    "areaServed": [
      { "@type": "City", "name": "Oviedo" },
      { "@type": "City", "name": "Gijón" },
      { "@type": "City", "name": "Avilés" },
      { "@type": "Country", "name": "España" }
    ],
    "description": "Servicio de proyectos personalizados con grabado láser e impresión 3D en Asturias. Desde la idea hasta el producto final. Asesoramiento profesional.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    }
  };

  const projectTypes = [
    {
      title: 'Regalos Personalizados',
      description: 'Crea regalos únicos y memorables para cualquier ocasión especial. Desde aniversarios hasta eventos corporativos.',
      icon: Gift,
      examples: ['Bodas', 'Bautizos', 'Cumpleaños', 'Empresas']
    },
    {
      title: 'Decoración e Interiorismo',
      description: 'Elementos decorativos personalizados que transforman cualquier espacio en algo único y especial.',
      icon: Home,
      examples: ['Cuadros', 'Lámparas', 'Letras', 'Señalética']
    },
    {
      title: 'Prototipos Técnicos',
      description: 'Valida diseños y fabrica piezas funcionales con precisión milimétrica para tus proyectos.',
      icon: Settings,
      examples: ['Piezas mecánicas', 'Carcasas', 'Soportes', 'Repuestos']
    },
    {
      title: 'Productos Corporativos',
      description: 'Artículos promocionales y elementos de branding que refuerzan la identidad de tu marca.',
      icon: Building2,
      examples: ['Trofeos', 'Merchandising', 'Señalética', 'Eventos']
    },
    {
      title: 'Arte y Diseño',
      description: 'Piezas artísticas únicas, esculturas y elementos creativos para coleccionistas y artistas.',
      icon: Palette,
      examples: ['Esculturas', 'Instalaciones', 'Piezas únicas', 'Ediciones']
    },
    {
      title: 'Ideas Creativas',
      description: '¿Tienes una idea diferente? Cuéntanos y la haremos realidad con nuestras tecnologías.',
      icon: MessageCircle,
      examples: ['Inventos', 'Proyectos DIY', 'Restauración', 'Custom']
    }
  ];

  const benefits = [
    {
      title: 'Asesoramiento Personalizado',
      description: 'Te guiamos en cada paso del proceso para garantizar el mejor resultado posible.',
      icon: Lightbulb
    },
    {
      title: 'Sin Límites Creativos',
      description: 'Si puedes imaginarlo, podemos crearlo. Desde lo más sencillo hasta lo más complejo.',
      icon: Sparkles
    },
    {
      title: 'Materiales Premium',
      description: 'Trabajamos con los mejores materiales para garantizar durabilidad y acabados profesionales.',
      icon: Gem
    },
    {
      title: 'Presupuesto Gratis',
      description: 'Te damos un presupuesto detallado antes de comenzar. Sin sorpresas ni compromiso.',
      icon: ClipboardList
    }
  ];

  const processSteps = [
    {
      icon: MessageCircle,
      title: '1. Consulta',
      description: 'Cuéntanos tu idea y necesidades. Te asesoramos sobre las mejores opciones para tu proyecto.'
    },
    {
      icon: PenTool,
      title: '2. Diseño',
      description: 'Preparamos bocetos y propuestas visuales. Ajustamos hasta que estés 100% satisfecho.'
    },
    {
      icon: CheckCircle2,
      title: '3. Validación',
      description: 'Revisamos juntos el diseño final y presupuesto. Solo comenzamos cuando todo esté perfecto.'
    },
    {
      icon: Send,
      title: '4. Producción',
      description: 'Fabricamos tu proyecto con la máxima calidad y te lo entregamos en el plazo acordado.'
    }
  ];

  return (
    <>
      <SEO 
        title="Proyecto Personalizado en Asturias"
        description="Servicio de proyectos personalizados con grabado láser e impresión 3D en Asturias. Desde la idea hasta el producto final. Asesoramiento profesional y presupuesto gratis."
        keywords="proyecto personalizado Asturias, proyecto personalizado Oviedo, proyecto personalizado Gijón, diseño personalizado, fabricación personalizada Asturias"
        canonicalUrl="https://artelaser3d.es/proyecto-personalizado"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="page-hero page-hero--proyecto">
        <div className="page-hero__container">
          <span className="page-hero__badge">Proyecto Personalizado</span>
          <h1 className="page-hero__title">
            De tu idea a la
            <span className="text-primary"> realidad</span>
          </h1>
          <p className="page-hero__description">
            Crea proyectos únicos y personalizados con grabado láser e impresión 3D. 
            Desde el concepto inicial hasta el producto final, te acompañamos en cada paso 
            con asesoramiento profesional.
          </p>
          <div className="page-hero__cta">
            <Link to="#formulario-proyecto" className="btn btn--white btn--lg">
              Empezar Mi Proyecto <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Project Types Section */}
      <section className="section section--project-types section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Tipos de Proyectos</span>
            <h2>¿Qué podemos crear para ti?</h2>
            <p className="section__description">
              Desde regalos personalizados hasta prototipos técnicos. 
              Explora las posibilidades que ofrecen nuestros servicios.
            </p>
          </header>

          <div className="project-types-grid-v2">
            {projectTypes.map((type, index) => (
              <article key={index} className="project-type-card-v2">
                <div className="project-type-card-v2__icon-wrapper">
                  <type.icon size={32} />
                </div>
                <h3>{type.title}</h3>
                <p>{type.description}</p>
                <div className="project-type-card-v2__tags">
                  {type.examples.map((example, i) => (
                    <span key={i} className="project-type-tag">{example}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section--proyecto-benefits-v2 section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Ventajas</span>
            <h2>¿Por qué elegirnos?</h2>
            <p className="section__description">
              Nos diferenciamos por nuestro compromiso con la calidad y el servicio personalizado.
            </p>
          </header>

          <div className="benefits-grid-v2">
            {benefits.map((benefit, index) => (
              <article key={index} className="benefit-card-v2">
                <div className="benefit-card-v2__icon">
                  <benefit.icon size={40} />
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section section--process-v2 section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Proceso</span>
            <h2>¿Cómo trabajamos?</h2>
            <p className="section__description">
              Un proceso sencillo y transparente para llevar tu idea a la realidad.
            </p>
          </header>

          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step-v3">
                <div className="process-step-v3__icon">
                  <step.icon size={28} />
                </div>
                <div className="process-step-v3__content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="process-step-v3__connector" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="formulario-proyecto" className="section section--form-v2 section--visible">
        <div className="section__container">
          <div className="form-section-wrapper">
            <div className="form-info">
              <span className="section__badge">Formulario</span>
              <h2>Cuéntanos tu proyecto</h2>
              <p>
                Completa el formulario con los detalles de tu idea. 
                Nos pondremos en contacto contigo en menos de 24 horas 
                con un presupuesto personalizado y sin compromiso.
              </p>
              
              <div className="form-info__features">
                <div className="form-info__feature">
                  <CheckCircle2 size={20} />
                  <span>Respuesta en 24h</span>
                </div>
                <div className="form-info__feature">
                  <CheckCircle2 size={20} />
                  <span>Asesoramiento gratuito</span>
                </div>
                <div className="form-info__feature">
                  <CheckCircle2 size={20} />
                  <span>Sin compromiso</span>
                </div>
              </div>
            </div>

            <div className="form-card-v2">
              <form onSubmit={handleSubmit} className="proyecto-form-v2">
                <div className="form-row-v2">
                  <div className="form-group-v2">
                    <label>Nombre completo *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      className={errors.nombre ? 'error' : ''}
                    />
                    {errors.nombre && <span className="form-error">{errors.nombre}</span>}
                  </div>

                  <div className="form-group-v2">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="form-error">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-group-v2">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+34 XXX XXX XXX"
                  />
                </div>

                <div className="form-group-v2">
                  <label>Tipo de proyecto *</label>
                  <select 
                    name="tipoProyecto" 
                    value={formData.tipoProyecto} 
                    onChange={handleChange}
                    className={errors.tipoProyecto ? 'error' : ''}
                  >
                    <option value="">Selecciona el tipo de proyecto</option>
                    <option value="regalo">Regalo Personalizado</option>
                    <option value="decoracion">Decoración e Interiorismo</option>
                    <option value="prototipo">Prototipo Técnico</option>
                    <option value="producto">Producto Corporativo</option>
                    <option value="arte">Arte y Diseño</option>
                    <option value="otro">Otro proyecto</option>
                  </select>
                  {errors.tipoProyecto && <span className="form-error">{errors.tipoProyecto}</span>}
                </div>

                <div className="form-group-v2">
                  <label>Descripción del proyecto *</label>
                  <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Cuéntanos todos los detalles de tu proyecto: dimensiones, materiales preferidos, colores, uso previsto..."
                    rows="5"
                    className={errors.descripcion ? 'error' : ''}
                  />
                  {errors.descripcion && <span className="form-error">{errors.descripcion}</span>}
                </div>

                <div className="checkbox-group-v2">
                  <input
                    type="checkbox"
                    id="aceptaTerminos"
                    name="aceptaTerminos"
                    checked={formData.aceptaTerminos}
                    onChange={handleChange}
                  />
                  <label htmlFor="aceptaTerminos">
                    Acepto la <Link to="/privacidad">política de privacidad</Link> y los <Link to="/terminos">términos de uso</Link> *
                  </label>
                </div>
                {errors.aceptaTerminos && <span className="form-error checkbox-error">{errors.aceptaTerminos}</span>}

                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                />

                {submitStatus && (
                  <div className={`form-status-message ${submitStatus.type}`}>
                    {submitStatus.message}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="btn btn--primary btn--lg btn--full"
                >
                  {isSubmitting ? (
                    'Enviando...'
                  ) : (
                    <>
                      Enviar Proyecto <Send size={20} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--cta section--cta--proyecto section--visible">
        <div className="cta__content">
          <h2 className="cta__title--proyecto">¿Tienes dudas?</h2>
          <p className="cta__description--proyecto">
            Si prefieres hablar directamente con nosotros antes de enviar el formulario, 
            no dudes en contactarnos. Estaremos encantados de resolver todas tus preguntas.
          </p>
          <div className="cta__buttons">
            <Link to="/contacto" className="btn btn--white btn--lg">
              Contactar
            </Link>
            <Link to="/preguntas-frecuentes" className="btn btn--outline btn--lg">
              Ver Preguntas Frecuentes
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProyectoPersonalizado;
